import React, { useMemo, useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { DateInputNew } from 'src/domains/layouts/shared/formComponents/dateInput/DateInputNew';
import { useCommon } from 'src/AppState/Common';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { DateTime } from 'src_common/utils/time/time';
import { apiGetSelfExcludeReportRequest } from 'src/api_openapi/v2/compliance/apiGetSelfExcludeReport';
import { ComplianceSelfExcludeReportRowType } from 'src/api_openapi/v2/compliance/schemas';
import { Button } from 'src/domains/common/components/button/button';
import { Alert } from 'src/domains/common/components/alert/alert';
import {
    AlertsWrapper,
    Card,
    CardTitle,
    ControlsRow,
    DateField,
    ReportsFilter,
    ReportsWrapper,
} from 'src/components/Reports/CompliancePlatformReports.style';

const isValidIsoDate = (value: string): boolean => {
    const parsed = DateTime.from(value);
    return parsed !== null;
};

const compareYmd = (a: string, b: string): number => {
    return a.localeCompare(b);
};

const buildCsv = (rows: ComplianceSelfExcludeReportRowType[]): string => {
    if (rows.length === 0) {
        return '';
    }

    const esc = (v: string | number): string => {
        const s = String(v);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    };

    const header = ['AccountID', 'AccountName', 'AccountEmail', 'Actor', 'SeDuration', 'SeStart', 'SeEnd'].join(',');

    const body = rows.map((row) =>
        [row.AccountID, row.AccountName, row.AccountEmail, row.Actor, row.SeDuration, row.SeStart, row.SeEnd]
            .map(esc)
            .join(',')
    );
    return [header, ...body].join('\n');
};

const downloadCsv = (csv: string, filename: string): void => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
    }, 0);
};

export const CompliancePlatformReports = observer('CompliancePlatformReports', (): React.ReactElement => {
    const common = useCommon();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const defaultFrom = useMemo(() => DateTime.current().subtractDays(7).format('YYYY-MM-DD'), []);
    const defaultTo = useMemo(() => DateTime.current().format('YYYY-MM-DD'), []);

    const [fromState] = useState(() => FormInputState.new(defaultFrom));
    const [toState] = useState(() => FormInputState.new(defaultTo));

    const isEnabled = common.appEnvParams.isGatewayActive;

    const fromYmd = DateTime.from(fromState.value)?.format('YYYY-MM-DD') ?? fromState.value;
    const toYmd = DateTime.from(toState.value)?.format('YYYY-MM-DD') ?? toState.value;

    const isFromValid = isValidIsoDate(fromYmd);
    const isToValid = isValidIsoDate(toYmd);
    const isRangeValid = isFromValid && isToValid && compareYmd(fromYmd, toYmd) <= 0;

    const onDownloadSelfExcludeCsv = async (): Promise<void> => {
        setError(null);

        if (!isEnabled) {
            setError('Compliance reports are not enabled (missing gateway).');
            return;
        }

        if (!isFromValid || !isToValid) {
            setError('Please provide valid from/to dates.');
            return;
        }

        if (!isRangeValid) {
            setError(`Invalid range: from (${fromYmd}) is after to (${toYmd}).`);
            return;
        }

        const startDate = `${fromYmd}T00:00:00Z`;
        const endDate = `${toYmd}T23:59:59Z`;

        setIsLoading(true);
        try {
            const response = await apiGetSelfExcludeReportRequest(
                common.appEnvParams.gatewayUrl,
                { startDate, endDate },
                { authorization: `Bearer ${common.session.currentJwt}` }
            );

            if (response.status !== 200) {
                if (response.status === 500) {
                    throw new Error(response.body.error.message);
                }
                throw new Error(response.body);
            }

            const csv = buildCsv(response.body.values);
            const filename = `self-exclude_${fromYmd}_${toYmd}.csv`;
            downloadCsv(csv, filename);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to fetch report.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ReportsWrapper>
            <ReportsFilter>
                <Card>
                    <CardTitle>Self-excluded accounts</CardTitle>
                    <ControlsRow>
                        <DateField>
                            <DateInputNew
                                stateValue={fromState}
                                selected={fromState.value}
                                label='from'
                            />
                        </DateField>
                        <DateField>
                            <DateInputNew
                                stateValue={toState}
                                selected={toState.value}
                                label='to'
                            />
                        </DateField>
                        <Button
                            type='button'
                            onClick={(): void => void onDownloadSelfExcludeCsv()}
                            disabled={isLoading || !isEnabled || !isRangeValid}
                            isLoading={isLoading}
                        >
                            Download Self-excluded CSV
                        </Button>
                    </ControlsRow>

                    <AlertsWrapper>
                        {isEnabled ? null : (
                            <Alert
                                variant='default'
                                title='Not available'
                                icon='info'
                            >
                                This tab requires `GATEWAY_API_HOST` for the current universe.
                            </Alert>
                        )}

                        {!isEnabled || isRangeValid ? null : (
                            <Alert
                                variant='default'
                                title='Check dates'
                                icon='info'
                            >
                                Please correct the date range to enable download.
                            </Alert>
                        )}

                        {error === null ? null : (
                            <Alert
                                variant='destructive'
                                title='Error'
                                icon='x'
                            >
                                {error}
                            </Alert>
                        )}
                    </AlertsWrapper>
                </Card>
            </ReportsFilter>

            {isLoading ? (
                <LoadingV2
                    title='Loading report...'
                    dataTest='compliance-platform-reports-loading'
                />
            ) : null}
        </ReportsWrapper>
    );
});
